import {
  getP32GlobalEvents,
  P28_GLOBAL_EVENTS,
  P3_GLOBAL_EVENTS,
  P5_GLOBAL_EVENTS,
  type EventInterval,
  type GlobalEvent,
} from '@lmt/signal-viewer-ui';
import {
  type ArchiveEdfManifest,
  type P5EdfManifestEntry,
  type TherapyGeneration,
} from '@lmt/signal-viewer-processor';
import { type TherapyEventDto } from '../api/events-api';
import {
  getP28TherapyArchive,
  getP32TherapyArchive,
  getP3TherapyArchive,
  getP5TherapyArchive,
  type TherapyArchiveResponse,
} from '../api/therapy-archive-api';
import { extractChannelLimits } from './channel-limits';
import { type FileUploadData } from './file-upload';
import {
  resolveSections,
  toTherapySettingsResult,
  type TherapyArchiveData,
} from './therapy-archive';

// yyyy-MM-ddTHH:mm:ss.FFF
const LOCAL_ISO_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/;

/** "5" → 500ms, "25" → 250ms, "123" → 123ms; undefined → 0. */
const fractionToMs = (fraction: string | undefined): number =>
  fraction ? Number(fraction.padEnd(3, '0')) : 0;

const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * Formats absolute ms as the offset-less local ISO wire convention (the inverse of
 * `localIsoToMs`); `toISOString()` would shift the wall-clock value to UTC. Returns
 * null for values the backend's `yyyy-MM-ddTHH:mm:ss.FFF` format cannot represent
 * (non-finite ms, years outside 1000-9999) — EDF headers are parsed leniently
 * enough that both occur.
 */
export const msToLocalIso = (ms: number): string | null => {
  if (!Number.isFinite(ms)) return null;

  const date = new Date(ms);
  const year = date.getFullYear();
  if (year < 1000 || year > 9999) return null;

  return (
    `${year}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` +
    `T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
  );
};

/**
 * Parses an offset-less ISO timestamp into absolute ms using local Date construction,
 * matching `parseEdfDateMs` in the processor's edf-reader so backend events align with
 * signal `startMs` in the browser's timezone.
 */
const localIsoToMs = (iso: string): number | null => {
  const match = LOCAL_ISO_REGEX.exec(iso);
  if (!match) return null;

  const [, year, month, day, hours, minutes, seconds, fraction] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
    fractionToMs(fraction),
  ).getTime();
};

/**
 * Maps backend therapy events into display-ready absolute-ms EventInterval[], with
 * the numeric `code` as `eventType` for channel binding. Device events bind under a
 * `device:` prefix: their id namespace is separate and would otherwise collide with
 * respiratory lanes (device id 1 vs. the eSO respiratory event id 1).
 */
export function backendEventsToIntervals(
  events: TherapyEventDto[],
): EventInterval[] {
  const intervals: EventInterval[] = [];

  events.forEach((event, index) => {
    const x1 = localIsoToMs(event.startLocal);
    const x2 = localIsoToMs(event.endLocal);
    if (x1 === null || x2 === null) return;

    const eventType =
      event.code == null
        ? undefined
        : event.kind === 'device'
          ? `device:${event.code}`
          : event.code.toString();

    intervals.push({
      x1,
      x2,
      isAlarm: event.isAlarm,
      eventKey: `backend:${index}`,
      eventType,
    });
  });

  return intervals;
}

/**
 * Global-event set matching the data's event-id space. Loose custom uploads
 * (no generation) parse with the P2.8 dialect, so its ids apply.
 */
export function getGlobalEventsForGeneration(
  generation: TherapyGeneration | undefined,
): GlobalEvent[] {
  switch (generation) {
    case 'p3':
      return P3_GLOBAL_EVENTS;
    case 'p32':
      return getP32GlobalEvents();
    case 'p5':
      return P5_GLOBAL_EVENTS;
    default:
      return P28_GLOBAL_EVENTS;
  }
}

const ARCHIVE_REQUEST_BY_CONTAINER: Partial<
  Record<
    FileUploadData['containerType'],
    (
      file: File,
      manifest: ArchiveEdfManifest,
    ) => Promise<TherapyArchiveResponse>
  >
> = {
  psdat: (file, manifest) => getP28TherapyArchive({ file, manifest }),
  pdat: (file, manifest) => getP3TherapyArchive({ file, manifest }),
  pvdat: (file, manifest) => getP32TherapyArchive({ file, manifest }),
  pldat: (file, manifest) =>
    getP5TherapyArchive({ file, manifest: requireP5Manifest(manifest) }),
};

const requireP5Manifest = (
  manifest: ArchiveEdfManifest,
): P5EdfManifestEntry[] => {
  if (manifest.every((entry) => 'p5DeviceTimepoint' in entry)) {
    return manifest as P5EdfManifestEntry[];
  }

  throw new Error('P5 archive requires the P5-specific EDF manifest.');
};

/**
 * Fetches backend-parsed therapy settings and events for an uploaded therapy archive
 * in one request, mapped to render-ready settings and alarm-limit segments. Returns
 * null for loose custom uploads (those post their paired XMLs via resolveCustomEvents
 * instead and have no settings). HTTP failures propagate — callers decide between
 * rethrowing and degrading.
 */
export async function getTherapyArchiveData(
  uploadedData: FileUploadData,
  edfManifest: ArchiveEdfManifest,
): Promise<TherapyArchiveData | null> {
  if (!uploadedData.sourceFile) {
    return null;
  }

  const fetchArchiveData =
    ARCHIVE_REQUEST_BY_CONTAINER[uploadedData.containerType];
  if (!fetchArchiveData) {
    return null;
  }

  const response = await fetchArchiveData(
    uploadedData.sourceFile,
    edfManifest,
  );
  const resolvedSections = response.therapySettings
    ? resolveSections(response.therapySettings)
    : null;

  return {
    therapySettings: resolvedSections
      ? toTherapySettingsResult(resolvedSections)
      : null,
    therapySettingsError: response.therapySettingsError,
    limits: resolvedSections ? extractChannelLimits(resolvedSections) : [],
    events: response.events,
    eventsError: response.eventsError,
  };
}

/**
 * Fetches backend-parsed events for an uploaded therapy archive. Returns null for
 * loose custom uploads (those post their paired XMLs via resolveCustomEvents
 * instead); failures degrade to "no events" so a backend outage never blocks an
 * upload.
 *
 * The null returns are load-bearing — never turn them into `[]`: the views merge
 * with `backendEvents ?? therapyData.events`, so an empty array here would wipe
 * the custom-upload events resolved at upload time in normalizeUpload.
 */
export async function getBackendEvents(
  uploadedData: FileUploadData,
  edfManifest: ArchiveEdfManifest,
): Promise<EventInterval[] | null> {
  try {
    const archiveData = await getTherapyArchiveData(
      uploadedData,
      edfManifest,
    );
    return archiveData?.events
      ? backendEventsToIntervals(archiveData.events)
      : null;
  } catch (error) {
    console.warn('Failed to load backend events for upload.', error);
    return null;
  }
}
